import { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import Head from 'next/head'
import classNames from 'classnames'

import { useAccents } from '@hooks/accents'
import { useQueryParams } from '@hooks/query-params'
import { Venue } from 'types/model'
import { IThemeContext } from 'types/contexts'
import { hexToRGB } from '@utils/colors'

const themeContext = createContext<IThemeContext>({} as IThemeContext)

export function useThemeContext() {
  return useContext(themeContext)
}

export function ThemeContext({
  children,
  venue: _venue,
  themeOverride,
  primaryColorOverride,
  secondaryColorOverride,
  widget = false,
}: {
  children: ReactNode
  venue?: Venue
  themeOverride?: 'light' | 'dark'
  primaryColorOverride?: string
  secondaryColorOverride?: string
  widget?: boolean
}) {
  const queryParams = useQueryParams()
  const [venue, setVenue] = useState<Venue | undefined>(_venue)

  const mode =
    themeOverride ||
    (queryParams.theme
      ? (queryParams.theme as 'light' | 'dark')
      : venue?.branding.lightContent ?? true
      ? 'light'
      : 'dark')

  const { primaryColor, secondaryColor } = venue?.branding ?? {
    primaryColor: { red: 120, green: 43, blue: 189 },
    secondaryColor: { red: 28, green: 28, blue: 28 },
  }

  const primaryColorOverrideRGB = hexToRGB(primaryColorOverride)
  const secondaryColorOverrideRGB = hexToRGB(secondaryColorOverride)

  const {
    red: primaryRed,
    green: primaryGreen,
    blue: primaryBlue,
  } = primaryColorOverrideRGB || primaryColor
  const {
    red: secondaryRed,
    green: secondaryGreen,
    blue: secondaryBlue,
  } = secondaryColorOverrideRGB || secondaryColor

  const primaryAccents = useAccents(primaryRed, primaryGreen, primaryBlue)
  const secondaryAccents = useAccents(secondaryRed, secondaryGreen, secondaryBlue)

  useEffect(() => {
    const html = document.querySelector('html')
    if (!widget) {
      html?.classList.add(mode)
    }

    const root = document.querySelector(':root') as HTMLElement
    root.style.setProperty('--primary-color-25', primaryAccents[25])
    root.style.setProperty('--primary-color-50', primaryAccents[50])
    root.style.setProperty('--primary-color-100', primaryAccents[100])
    root.style.setProperty('--primary-color-200', primaryAccents[200])
    root.style.setProperty('--primary-color-300', primaryAccents[300])
    root.style.setProperty('--primary-color-400', primaryAccents[400])
    root.style.setProperty('--primary-color-500', primaryAccents[500])
    root.style.setProperty('--primary-color-600', primaryAccents[600])
    root.style.setProperty('--primary-color-700', primaryAccents[700])
    root.style.setProperty('--primary-color-800', primaryAccents[800])
    root.style.setProperty('--primary-color-900', primaryAccents[900])

    root.style.setProperty('--secondary-color-25', secondaryAccents[25])
    root.style.setProperty('--secondary-color-50', secondaryAccents[50])
    root.style.setProperty('--secondary-color-100', secondaryAccents[100])
    root.style.setProperty('--secondary-color-200', secondaryAccents[200])
    root.style.setProperty('--secondary-color-300', secondaryAccents[300])
    root.style.setProperty('--secondary-color-400', secondaryAccents[400])
    root.style.setProperty('--secondary-color-500', secondaryAccents[500])
    root.style.setProperty('--secondary-color-600', secondaryAccents[600])
    root.style.setProperty('--secondary-color-700', secondaryAccents[700])
    root.style.setProperty('--secondary-color-800', secondaryAccents[800])
    root.style.setProperty('--secondary-color-900', secondaryAccents[900])

    return () => {
      if (!widget) {
        html?.classList.remove(mode)
      }
    }
  }, [primaryAccents, mode, secondaryAccents, widget])

  return (
    <themeContext.Provider
      value={{ setVenue, mode, colors: { primary: primaryAccents, secondary: secondaryAccents } }}
    >
      <Head>
        <style jsx global>{`
          :root {
            --primary-color-25: ! ${primaryAccents[25]};
            --primary-color-50: ! ${primaryAccents[50]};
            --primary-color-100: ! ${primaryAccents[100]};
            --primary-color-200: ! ${primaryAccents[200]};
            --primary-color-300: ! ${primaryAccents[300]};
            --primary-color-400: ! ${primaryAccents[400]};
            --primary-color-500: ! ${primaryAccents[500]};
            --primary-color-600: ! ${primaryAccents[600]};
            --primary-color-700: ! ${primaryAccents[700]};
            --primary-color-800: ! ${primaryAccents[800]};
            --primary-color-900: ! ${primaryAccents[900]};

            --primary-color-25: ! ${primaryAccents[25]};
            --primary-color-50: ! ${primaryAccents[50]};
            --primary-color-100: ! ${primaryAccents[100]};
            --primary-color-200: ! ${primaryAccents[200]};
            --primary-color-300: ! ${primaryAccents[300]};
            --primary-color-400: ! ${primaryAccents[400]};
            --primary-color-500: ! ${primaryAccents[500]};
            --primary-color-600: ! ${primaryAccents[600]};
            --primary-color-700: ! ${primaryAccents[700]};
            --primary-color-800: ! ${primaryAccents[800]};
            --primary-color-900: ! ${primaryAccents[900]};
          }
        `}</style>
        <meta
          name="theme-color"
          content={`#${primaryRed.toString(16)}${primaryGreen.toString(16)}${primaryBlue.toString(
            16
          )}`}
        />
      </Head>
      <div className={classNames('theme-context', mode)}>
        <div className={classNames({ 'background-basic': !widget })}>{children}</div>
      </div>
    </themeContext.Provider>
  )
}
